import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
import lodashGet from "lodash/get";
import lodashCloneDeep from "lodash/cloneDeep";
import lodashKebabCase from "lodash/kebabCase";
import lodashUpperFirst from "lodash/upperFirst";

import { getTodayStartEndDate } from "@/common/date.utilities";
import { getKeywordsStringFromBuilder } from "@/services/search/savedSearch.service";
import { SOURCE_METADATA } from "@/common/constants/search";
import { ACCEPTED_PARAMS } from "@/common/constants/apiUtilities/acceptedParams";
import { isFeatureUnlocked } from "@/common/featureLock";

export { _formatSavedSearchParams as formatSavedSearchParams };
export { _setSourceMetadataDynamicConstants as setSourceMetadataDynamicConstants };

const _formatSavedSearchParams = (params) => {
  let _format;
  if (params) {
    const oParams = lodashCloneDeep(params);
    _format = {
      search: oParams.search,
      start: oParams.start,
      boundsType: oParams.boundsType,
      end: oParams.end ? oParams.end : getTodayStartEndDate(dayjs.utc()).end,
      time_range: oParams.time_range ? oParams.time_range : null,
      places: oParams.places,
      assets: oParams.assets,
      sentiments: oParams.sentiments,
      placesDistUnits: "km",
      emotions: oParams.emotions,
    };

    if (oParams.bounds && oParams.boundsType === "mapextent") {
      _format.bounds = oParams.bounds;
    }

    if (oParams.emotions && Array.isArray(oParams.emotions)) {
      _format.emotions = oParams.emotions.join();
    }
    if (oParams.sentiments && Array.isArray(oParams.sentiments)) {
      _format.sentiments = oParams.sentiments.join();
    }

    _format.source_metadata = _format.source_metadata || {};
    SOURCE_METADATA.forEach((type) => {
      if (
        oParams.sourceMetadata?.[type] &&
        Array.isArray(oParams.sourceMetadata[type])
      ) {
        _format.source_metadata[type] = oParams.sourceMetadata[type];
      }
    });

    if (
      lodashGet(oParams.keywordsBuilder, "includes[0].keywords.length") ||
      lodashGet(oParams.keywordsBuilder, "excludes.length")
    ) {
      _format.keywordsBuilder = oParams.keywordsBuilder;
      _format.search = getKeywordsStringFromBuilder(oParams.keywordsBuilder);
    }
    if (oParams.aoiId && oParams.aoiId.length) {
      _format.aoiId = oParams.aoiId;
    }
    if (oParams.polygon) {
      _format.polygon = oParams.polygon;
    }
    if (oParams.topics) {
      _format.topics = oParams.topics.split(",");
    }
    if (oParams.sources) {
      _format.sources = oParams.sources.split(",");
    }
    if (oParams.hotspotsOnly) {
      oParams.hotspotsOnly =
        oParams.hotspotsOnly === "true" || oParams.hotspotsOnly === true;
      _format.hotspotsOnly = oParams.hotspotsOnly;
    }
    if (oParams.eventType) {
      _format.eventType = oParams.eventType;
    }
    if (oParams.mapType) {
      _format.mapType = oParams.mapType;
    }

    if (oParams.verifiedType !== "maritime") {
      if (
        oParams.severityMin &&
        !isNaN(oParams.severityMin) &&
        oParams.severityMin !== 0
      ) {
        _format.severityMin = oParams.severityMin;
      }
      if (!isNaN(oParams.severityMax) && oParams.severityMax !== 10) {
        _format.severityMax = oParams.severityMax;
      }
    }

    if (oParams.verifiedType) {
      _format.verifiedType = oParams.verifiedType;
      if (oParams.verifiedType !== "maritime") {
        if (oParams.sectors) {
          _format.sectors = !Array.isArray(oParams.sectors)
            ? oParams.sectors.split(",")
            : oParams.sectors;
        }
        if (oParams.veAssets) {
          _format.veAssets = !Array.isArray(oParams.veAssets)
            ? oParams.veAssets.split(",")
            : oParams.veAssets;
        }
        if (oParams.attackTypes) {
          _format.attackTypes = !Array.isArray(oParams.attackTypes)
            ? oParams.attackTypes.split(",")
            : oParams.attackTypes;
        }
        if (oParams.perpetrators) {
          _format.perpetrators = !Array.isArray(oParams.perpetrators)
            ? oParams.perpetrators.split(",")
            : oParams.perpetrators;
        }
      } else {
        if (oParams.maritimeSubcategories) {
          _format.maritimeSubcategories = !Array.isArray(
            oParams.maritimeSubcategories
          )
            ? oParams.maritimeSubcategories.split(",")
            : oParams.maritimeSubcategories;
        }
        if (oParams.maritimeVesselTypes) {
          _format.maritimeVesselTypes = !Array.isArray(
            oParams.maritimeVesselTypes
          )
            ? oParams.maritimeVesselTypes.split(",")
            : oParams.maritimeVesselTypes;
        }
      }
    }
    if (oParams.riskCategories) {
      _format.riskCategories = oParams.riskCategories.split(",");
    }
    if (oParams.tags) {
      _format.tags = oParams.tags.split(",");
    }

    if (oParams.geolocationPrecisions) {
      _format.geolocationPrecisions = oParams.geolocationPrecisions;
    }

    if (Array.isArray(oParams.influencees) && oParams.influencees.length > 0) {
      _format.influencees = oParams.influencees;
    }
    if (Array.isArray(oParams.influencers) && oParams.influencees.length > 0) {
      _format.influencers = oParams.influencers;
    }
  }
  return _format;
};

function _setSourceMetadataDynamicConstants() {
  SOURCE_METADATA.forEach((type) => {
    if (
      isFeatureUnlocked(
        `livesearch-source-metadata-${lodashKebabCase(type)}`
      ) &&
      //Platform is covered by source
      type !== "platform"
    ) {
      ACCEPTED_PARAMS.push(`sourceMetadata${lodashUpperFirst(type)}`);
    }
  });
}
